/**
 * What came back, for the five people who may read it.
 *
 * Two views of the same rows and both are necessary. The aggregate is what
 * goes in a meeting — a median, a count, a distribution. The individual
 * responses are what changes one, because a sentence somebody wrote about why
 * they are leaving does work no percentage does.
 *
 * Numbers lead with the median rather than the mean, everywhere. One person
 * reporting the cap moves a mean of sixty by a whole hour, and the figure that
 * goes in front of an employer should be the one that survives being argued
 * with. The mean is shown beside it because the gap between them is itself
 * information: a mean well above the median means a tail, and the tail is
 * usually a faculty.
 *
 * Free text is shown in full and never summarised into a count. Every other
 * surface in this product exists to stop generated prose being mistaken for
 * source material, and the same rule holds here: what a colleague wrote is the
 * evidence, and a paraphrase of it is not.
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { surveyLink, type Question, type Section } from '@tmea/shared';
import { api, type SurveyResults } from '../lib/api.js';

/**
 * Two overlapping rectangles, drawn rather than typed.
 *
 * Same reasoning as the paperclip in Ask: the glyph guard forbids
 * Extended_Pictographic characters in components, an emoji does not take the
 * surrounding weight and does not survive a print, and a dependency to draw
 * twelve pixels is a dependency to audit. currentColor inherits the
 * muted-to-ink hover the rest of the surface uses.
 */
function CopyIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

/**
 * The link, whole, with one button that puts it on the clipboard.
 *
 * It printed `#/survey/staff-2026` — the tail of an address rather than an
 * address. A representative pasting that into an email to a hundred and twenty
 * people sends something nobody can open, and it looks close enough to right to
 * go out unread.
 *
 * Shown while responses are coming in as well as before any arrive, because the
 * link is needed for exactly as long as the survey is open, and the empty state
 * is the one screen a representative stops visiting the moment it stops being
 * empty.
 *
 * The address stays visible and selectable rather than living only inside the
 * button. navigator.clipboard is unavailable on an insecure origin and can be
 * refused by permissions, so a copy control that has swallowed the text is a
 * control that can strand you with no way to get it — the button is the
 * convenience and the text is the guarantee.
 */
function ShareLink({ slug }: { slug: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const href = surveyLink(window.location.origin, slug);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(href);
      setState('copied');
      window.setTimeout(() => setState('idle'), 2000);
    } catch {
      // Says what to do instead rather than failing silently, which on a
      // control whose whole job is one action reads as the app being broken.
      setState('failed');
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <code
        className="clause-number select-all rounded-[6px] border px-2 py-1 text-[12px]"
        style={{ borderColor: 'var(--color-rule)', background: 'var(--color-subtle)' }}
      >
        {href}
      </code>
      <button
        type="button"
        onClick={() => void copy()}
        className="hit label inline-flex items-center gap-1.5 rounded-[6px] border px-2 py-1"
        style={{ borderColor: 'var(--color-rule)' }}
        aria-label="Copy the survey link"
      >
        <CopyIcon />
        Copy
      </button>
      {/*
        Polite, and always present rather than mounted on success: a live region
        added to the page at the moment it gains text is frequently not
        announced at all.
      */}
      <span role="status" aria-live="polite" className="label">
        {state === 'copied' ? 'Copied' : state === 'failed' ? 'Press to select, then copy' : ''}
      </span>
    </div>
  );
}

export function Responses({ slug }: { slug: string }) {
  const results = useQuery({ queryKey: ['survey-responses', slug], queryFn: () => api.surveyResponses(slug) });
  const [view, setView] = useState<'aggregate' | 'individual'>('aggregate');

  if (results.isPending) return <p className="label p-6">Loading…</p>;
  if (!results.data) return <p className="label p-6">No survey by that name.</p>;

  const data = results.data;

  if (data.total === 0) {
    return (
      <div className="screen-column">
        <h1 className="register-reading text-[21px]">{data.title}</h1>
        <p className="measure mt-3 text-[14px] leading-relaxed text-[var(--color-muted)]">
          Nothing has come back yet. Send secondary staff this link:
        </p>
        <div className="mt-3">
          <ShareLink slug={slug} />
        </div>
      </div>
    );
  }

  return (
    <div className="screen-column">
      <header className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <h1 className="register-reading m-0 text-[21px] leading-snug">{data.title}</h1>
          {/* No breakdown by role: everybody answering is a teacher, so it
              was the total printed twice. */}
          <p className="label mt-1">{data.total} responses</p>
          <div className="mt-2.5">
            <ShareLink slug={slug} />
          </div>
        </div>
        <div className="flex gap-1.5">
          {(['aggregate', 'individual'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              aria-pressed={view === v}
              className="hit label rounded-[6px] border px-2.5 py-1.5"
              style={{
                borderColor: view === v ? 'var(--color-ink)' : 'var(--color-rule)',
                background: view === v ? 'var(--color-subtle)' : undefined,
              }}
            >
              {v === 'aggregate' ? 'Aggregate' : 'Individual'}
            </button>
          ))}
        </div>
      </header>

      {/*
        Said once, at the top, where it cannot be missed.

        The undertaking to sixty colleagues was that this goes to the
        representatives and is deleted at the end. Free text will identify
        people whatever the schema does — "as the only Physics teacher" is a
        name — and the team needs that in front of them before they read it,
        not in a policy document nobody opens.
      */}
      <p className="measure mt-5 rounded-[8px] border border-[var(--color-rule-strong)] px-3 py-2.5 text-[12.5px] leading-relaxed">
        These answers carry no name. Some will identify their author anyway, by what they describe.
        They were given on the understanding that the bargaining team reads them and nobody else,
        and that they are deleted at the end of the process.
      </p>

      <Narrative slug={slug} />

      {view === 'aggregate' ? (
        <Aggregate data={data} />
      ) : (
        <Individual data={data} />
      )}
    </div>
  );
}

function Aggregate({ data }: { data: SurveyResults }) {
  return (
    <div className="mt-2">
      {data.sections.map((section: Section) => (
        <section key={section.id} className="section-rule mt-7 pt-5">
          <h2 className="label m-0">{section.title}</h2>
          <div className="mt-4 flex flex-col gap-6">
            {section.questions.map((q: Question) => (
              <QuestionResult key={q.id} question={q} stat={data.aggregate[q.id]} total={data.total} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function QuestionResult({
  question,
  stat,
  total,
}: {
  question: Question;
  stat: SurveyResults['aggregate'][string] | undefined;
  total: number;
}) {
  if (!stat || stat.answered === 0) {
    return (
      <div>
        <p className="text-[13.5px] leading-snug">{question.prompt}</p>
        <p className="label mt-1 text-[10.5px] text-[var(--color-muted)]">Nobody answered this</p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-[13.5px] leading-snug">{question.prompt}</p>
      {/*
        The denominator is per question, not per survey. Aides never saw the
        pay section, so "12 of 60" there would suggest 48 people declined to
        answer something they were never shown.
      */}
      <p className="label mt-1 text-[10.5px] text-[var(--color-muted)]">
        {stat.answered} answered{stat.answered < total ? ` of ${total} responses` : ''}
      </p>

      {stat.kind === 'number' ? (
        <p className="mt-2 flex flex-wrap items-baseline gap-3">
          <span className="clause-number text-[22px] font-semibold">{stat.median}</span>
          <span className="label">median</span>
          <span className="label text-[var(--color-muted)]">
            mean {stat.mean} · range {stat.min}–{stat.max}
          </span>
        </p>
      ) : null}

      {stat.counts ? (
        <dl className="mt-2 flex flex-col gap-1">
          {Object.entries(stat.counts)
            .sort((a, b) => b[1] - a[1])
            .map(([value, n]) => (
              <div key={value} className="flex items-center gap-3">
                <dt className="w-44 shrink-0 truncate text-[12.5px]">{labelFor(question, value)}</dt>
                {/* A bar, so a distribution is read rather than computed. */}
                <dd className="m-0 flex flex-1 items-center gap-2">
                  <span
                    className="inline-block h-2 rounded-[2px]"
                    style={{
                      width: `${Math.max(2, (n / stat.answered) * 100)}%`,
                      background: 'var(--color-ink)',
                    }}
                  />
                  <span className="clause-number text-[11px]">{n}</span>
                </dd>
              </div>
            ))}
        </dl>
      ) : null}

      {stat.texts ? (
        <div className="mt-2 flex flex-col gap-2">
          {stat.texts.map((text, i) => (
            <p
              key={i}
              className="measure rounded-[6px] border-l-2 border-[var(--color-rule-strong)] pl-3 text-[13px] leading-relaxed"
            >
              {text}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One person's answers, whole.
 *
 * Kept as a set rather than flattened into the aggregate because a response
 * reads differently as a whole than as thirty separate data points — somebody
 * who wants more time for pastoral care, has been interviewing elsewhere, and
 * wrote a paragraph about their faculty is a story, and the story is what a
 * representative takes into a meeting.
 */
function Individual({ data }: { data: SurveyResults }) {
  const [open, setOpen] = useState<string | null>(data.responses[0]?.id ?? null);
  const prompts = new Map<string, string>(
    data.sections.flatMap((s: Section) => s.questions.map((q: Question) => [q.id, q.prompt] as const)),
  );

  return (
    <div className="mt-6 flex flex-col gap-2">
      {data.responses.map((r, i) => (
        <div key={r.id} className="rounded-[8px] border border-[var(--color-rule)]">
          <button
            type="button"
            onClick={() => setOpen(open === r.id ? null : r.id)}
            aria-expanded={open === r.id}
            className="hit flex w-full items-baseline gap-3 px-3 py-2.5 text-left"
          >
            {/*
              Numbered by position, never by id. A uuid on screen is a handle
              somebody could paste into a message, and the point of this table
              is that a response cannot be traced to a person.
            */}
            <span className="clause-number text-[11px]">#{i + 1}</span>
            {/* No role beside the number. It read "Teachers" on every row, which
                is a column that distinguishes nothing and reads as though it
                might. */}
            <span className="label ml-auto text-[var(--color-muted)]">{r.submittedOn}</span>
          </button>

          {open === r.id ? (
            <dl className="border-t border-[var(--color-rule)] px-3 py-3">
              {Object.entries(r.answers).map(([id, value]) => (
                <div key={id} className="mb-3 last:mb-0">
                  <dt className="label text-[10.5px] text-[var(--color-muted)]">
                    {prompts.get(id) ?? id}
                  </dt>
                  <dd className="measure m-0 mt-0.5 text-[13.5px] leading-relaxed">
                    {Array.isArray(value) ? value.join(' · ') : String(value)}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** A choice's own words, falling back to the stored value. */
function labelFor(question: Question, value: string): string {
  if (question.kind === 'choice' || question.kind === 'multi' || question.kind === 'rank') {
    return question.choices.find((c) => c.value === value)?.label ?? value;
  }
  return value;
}

/**
 * A reading of the responses, and a claim from it.
 *
 * Behind a button rather than on load, for two reasons. A narrative over a
 * survey that is still open is stale the moment somebody else answers, and
 * generated prose that appears without being asked for is generated prose
 * somebody will quote without noticing where it came from.
 *
 * Marked as model-written wherever it appears, and set apart from the figures
 * above it — those are counted, this is composed, and the difference is the
 * whole reason this product keeps its registers separate.
 */
function Narrative({ slug }: { slug: string }) {
  const write = useMutation({ mutationFn: () => api.surveyNarrative(slug) });
  const [claiming, setClaiming] = useState(false);
  const [title, setTitle] = useState('');
  const [type, setType] = useState('new');
  const claim = useMutation({
    mutationFn: () =>
      api.claimFromSurvey(slug, { title, type, evidence: write.data?.narrative ?? '' }),
  });

  return (
    <section className="section-rule mt-7 pt-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="label m-0">What this says, read together</h2>
        <button
          type="button"
          onClick={() => write.mutate()}
          disabled={write.isPending}
          className="hit label rounded-[6px] border border-[var(--color-rule-strong)] px-2.5 py-1.5"
        >
          {write.isPending ? 'Reading…' : write.data ? 'Read again' : 'Summarise'}
        </button>
      </div>

      {write.isError ? (
        <p role="alert" className="mt-3 text-[13px] text-[var(--color-defect)]">
          {(write.error as Error).message}
        </p>
      ) : null}

      {write.data?.narrative ? (
        <>
          <p className="label mt-3 text-[10.5px] text-[var(--color-muted)]">
            Written by a model from the figures above, over {write.data.responses} responses. It is
            not evidence — the answers are.
          </p>
          <div className="register-reading mt-2">
            {write.data.narrative.split(/\n{2,}/).map((para, i) => (
              <p key={i} className="measure mb-3 text-[14px] leading-relaxed last:mb-0">
                {para}
              </p>
            ))}
          </div>

          {claim.data ? (
            <p className="label mt-4">Claim {claim.data.ref} is on the board, as a draft.</p>
          ) : claiming ? (
            <div className="mt-4 flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1">
                <span className="label">Title</span>
                <input
                  aria-label="Title for the claim built from this survey"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-80 rounded-[6px] border border-[var(--color-rule-strong)] px-2.5 py-1.5 text-[13.5px] outline-none"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="label">Kind</span>
                <select
                  aria-label="What kind of claim this is"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="rounded-[6px] border border-[var(--color-rule-strong)] px-2.5 py-1.5 text-[13.5px]"
                >
                  {['new', 'improve', 'protect', 'correct'].map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={() => claim.mutate()}
                disabled={title.trim().length < 3 || claim.isPending}
                className="hit rounded-[6px] px-3 py-1.5 text-[13px] disabled:opacity-40"
                style={{ background: 'var(--color-ink)', color: 'var(--color-paper)' }}
              >
                {claim.isPending ? 'Adding…' : 'Add the claim'}
              </button>
              {claim.isError ? (
                <span className="text-[12px] text-[var(--color-defect)]">
                  {(claim.error as Error).message}
                </span>
              ) : null}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setClaiming(true)}
              className="hit label mt-4 underline underline-offset-2"
            >
              Make this a claim
            </button>
          )}
        </>
      ) : null}

      {write.data && write.data.narrative === null ? (
        <p className="measure mt-3 text-[13px] leading-relaxed text-[var(--color-muted)]">
          Nothing could be written. The figures above are unaffected.
        </p>
      ) : null}
    </section>
  );
}
